import { html, unsafeCSS } from 'lit';

import { property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { DERElement } from '~/common/der-element';
import { LocaleCode } from '~/common/interfaces';
import '~/ui/components/button';
import '~/ui/components/link';
import { styles as globalStyles } from '~/ui/scss/main.styles';
import { SvgUtility } from '~/ui/utils';
import { ProgressBarItem } from './progress-bar-item/progress-bar-item.component';
import { ProgressBarButtonConfig, ProgressBarLinkConfig } from './progress-bar-item/types';
import { styles } from './progress-bar.styles';

enum ProgressDirection {
    Back = 'back',
    Next = 'next',
}

export class ProgressBar extends DERElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: Number, attribute: 'index' }) currentIndex = 0;
    @property({ type: String, attribute: 'back-button-label' }) backButtonLabel = '';
    @property({ type: String, attribute: 'next-button-label' }) nextButtonLabel = '';
    @property({ type: String }) override lang = LocaleCode.DE_CH;

    @state()
    private _currentItem?: ProgressBarItem;

    @state()
    private _itemIsValid = true;

    @queryAssignedElements({ selector: 'der-ui-progress-bar-item' })
    items!: Array<ProgressBarItem>;

    @query('.c-progress-bar__progress')
    private _progressBarElement!: HTMLElement;

    // Bound handler so we can properly remove it
    private _labelChangeHandler = () => this.requestUpdate();

    override connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener('der.progressbar-item-validity', this._validityEventHandler as EventListener);
        // Ensure we re-render when child item labels change asynchronously (e.g., translations loaded)
        this.addEventListener('der.progressbar-item-label-change', this._labelChangeHandler);
    }

    override disconnectedCallback(): void {
        this.removeEventListener('der.progressbar-item-validity', this._validityEventHandler as EventListener);
        this.removeEventListener('der.progressbar-item-label-change', this._labelChangeHandler);
        super.disconnectedCallback();
    }

    handleSlotchange() {
        this._setIndex(this.currentIndex);
    }

    private _emitControlEvent(direction: ProgressDirection) {
        const details = {
            direction,
            index: this.currentIndex,
        };
        this._emitEvent('der.progress-bar-control-click', details);
    }

    private _emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    private _validityEventHandler(e: CustomEvent<{ valid: boolean }>) {
        this._itemIsValid = e.detail.valid;
    }

    private _showPrevItem() {
        this._currentItem?.backButton?.clickFn?.();
        this._setIndex(this.currentIndex - 1);
        this._emitControlEvent(ProgressDirection.Back);
    }

    showNextItem() {
        if (this._currentItem?.nextButton?.clickFn && !this._currentItem.nextButton.clickFn()) {
            return;
        }
        this._setIndex(this.currentIndex + 1);
        this._emitControlEvent(ProgressDirection.Next);
    }

    private _setIndex(index: number) {
        const maxIndex = this.items.length - 1;
        const checkedIndex = Math.max(0, Math.min(index, maxIndex));
        const indexChanged = this.currentIndex !== checkedIndex;
        this.currentIndex = checkedIndex;
        this.items.forEach((item, i) => {
            const isActive = i === checkedIndex;
            item.classList?.toggle('inactive', !isActive);
            item.setCurrentItemState(isActive);

            if (isActive) {
                this._currentItem = item;
            }
        });
        this.requestUpdate();
        if (indexChanged) {
            this._progressBarElement.scrollIntoView({ behavior: 'smooth' });
        }
    }

    override render() {
        return html`<div class="c-progress-bar">
                <div class="c-progress-bar__progress">
                    ${this.items.map(
                        (item, i) =>
                            html`<div
                                class=${classMap({
                                    prev: this.currentIndex > i,
                                    current: this.currentIndex == i,
                                    next: this.currentIndex < i,
                                })}
                            >
                                ${this.currentIndex <= i
                                    ? html`<button tabindex="-1">${i + 1}</button> `
                                    : html`<button class="prev" @click=${() => this._setIndex(i)}>
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
                                              ${SvgUtility.systemCheck}
                                          </svg>
                                      </button>`}
                                <div class="c-progress-bar__label">${item.label}</div>
                            </div>`
                    )}
                </div>
            </div>
            <div class="c-progress-bar__content">
                <slot @slotchange=${this.handleSlotchange}></slot>
            </div>
            <div class="c-progress-bar__controls">
                <div class="prev">${this._renderControl(ProgressDirection.Back, this._currentItem?.backButton)}</div>
                <div class="next">${this._renderControl(ProgressDirection.Next, this._currentItem?.nextButton)}</div>
            </div>`;
    }

    private _renderControl(
        direction: ProgressDirection,
        customElementConfig?: ProgressBarLinkConfig | ProgressBarButtonConfig
    ) {
        const isDirectionBack = direction === ProgressDirection.Back;
        const isDirectionNext = direction === ProgressDirection.Next;
        const backButtonLabel = this.backButtonLabel || this.lang === LocaleCode.DE_CH ? 'Zurück' : 'retour';
        const nextButtonLabel = this.nextButtonLabel || this.lang === LocaleCode.DE_CH ? 'Weiter' : 'continuer';
        const label = customElementConfig?.config?.label || (isDirectionBack ? backButtonLabel : nextButtonLabel);
        const isHidden =
            (isDirectionBack && (!!this._currentItem?.backButton?.hidden || this.currentIndex === 0)) ||
            (isDirectionNext && !!this._currentItem?.nextButton?.hidden);

        if (customElementConfig?.type === 'button') {
            const {
                icon,
                iconPosition = 'before',
                type = 'button',
                url,
                variant = 'primary',
                wrapText,
            } = customElementConfig?.config || {};

            return html`<der-ui-button
                @click="${isDirectionBack ? this._showPrevItem : this.showNextItem}"
                variant=${variant}
                .icon=${icon}
                iconPosition=${iconPosition}
                type=${type}
                .wrapText=${wrapText}
                .url=${url}
                class=${classMap({
                    hidden: isHidden,
                })}
                >${label}</der-ui-button
            > `;
        } else {
            const config = customElementConfig?.config;
            const svg = config?.svg || (isDirectionBack ? 'systemArrowLeftLong' : 'systemArrowRightLong');
            const svgPosition = config?.svgPosition || (isDirectionBack ? 'before' : 'after');

            const { url, variant, wrapText } = config || {};

            return html`<der-ui-link
                @click="${isDirectionBack ? this._showPrevItem : this.showNextItem}"
                svg=${svg}
                svg-position="${svgPosition}"
                .variant=${variant}
                .url=${url}
                .wrapText=${wrapText}
                class=${classMap({
                    hidden: isHidden,
                })}
                >${label}</der-ui-link
            > `;
        }
    }
}
